import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { PromoActionDto, PromoCreateDto, PromoDto } from '../../../types/admin.types';
import { createAdminPromo, deleteAdminPromo, fetchAdminPromos, updateAdminPromo } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import ImagePickerField from './components/ImagePickerField';
import AdminDateTimeInput from './components/AdminDateTimeInput';
import { toDateTimeLocalInput, toUtcIsoString } from '../../../utils/date-time';

const promoActionTypes = ['BUY_PPV', 'BUY_TICKET', 'SHARE', 'VISIT', 'WATCH_TRAILER'];
const promoTargetTypes = ['internal', 'external'];

const emptyForm: PromoCreateDto = {
    title: '',
    description: '',
    imageUrl: '',
    startDate: '',
    endDate: '',
    isActive: true,
    sortOrder: 0,
    cta: []
};

const AdminPromos = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<PromoDto[]>([]);
    const [form, setForm] = useState<PromoCreateDto>(emptyForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const token = getUserInfo()?.token;

    const mapPromo = (item: any): PromoDto => ({
        id: item.id ?? item.Id ?? '',
        title: item.title ?? item.Title ?? '',
        description: item.description ?? item.Description ?? null,
        imageUrl: item.imageUrl ?? item.ImageUrl ?? '',
        startDate: item.startDate ?? item.StartDate ?? null,
        endDate: item.endDate ?? item.EndDate ?? null,
        createdDate: item.createdDate ?? item.CreatedDate ?? '',
        updatedDate: item.updatedDate ?? item.UpdatedDate ?? '',
        isActive: item.isActive ?? item.IsActive ?? false,
        sortOrder: item.sortOrder ?? item.SortOrder ?? 0,
        cta: (item.cta ?? item.Cta ?? []).map((action: any) => ({
            type: action.type ?? action.Type ?? '',
            label: action.label ?? action.Label ?? '',
            targetType: action.targetType ?? action.TargetType ?? '',
            target: action.target ?? action.Target ?? ''
        }))
    });

    const loadItems = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchAdminPromos(token);
            setItems((response ?? []).map(mapPromo));
        } catch (error) {
            setErrorMessage('Unable to load promos.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
    };

    const handleEdit = (item: PromoDto) => {
        setSelectedId(item.id);
        setForm({
            title: item.title,
            description: item.description ?? '',
            imageUrl: item.imageUrl,
            startDate: toDateTimeLocalInput(item.startDate),
            endDate: toDateTimeLocalInput(item.endDate),
            isActive: item.isActive,
            sortOrder: item.sortOrder,
            cta: item.cta ?? []
        });
    };

    const handleActionChange = (index: number, field: keyof PromoActionDto, value: string) => {
        const nextActions = [...form.cta];
        nextActions[index] = { ...nextActions[index], [field]: value };
        setForm({ ...form, cta: nextActions });
    };

    const addAction = () => {
        setForm({
            ...form,
            cta: [...form.cta, { type: promoActionTypes[0], label: '', targetType: promoTargetTypes[0], target: '' }]
        });
    };

    const removeAction = (index: number) => {
        const nextActions = [...form.cta];
        nextActions.splice(index, 1);
        setForm({ ...form, cta: nextActions });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        const payload: PromoCreateDto = {
            ...form,
            startDate: toUtcIsoString(form.startDate),
            endDate: toUtcIsoString(form.endDate)
        };

        try {
            if (selectedId) {
                await updateAdminPromo(selectedId, payload, token);
            } else {
                await createAdminPromo(payload, token);
            }
            await loadItems();
            resetForm();
        } catch (error: any) {
            setErrorMessage(error?.message || 'Unable to save promo.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteAdminPromo(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems();
        } catch (error) {
            setErrorMessage('Unable to delete promo.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Promos</h1>
                    <p>Manage homepage promos and CTA buttons.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Current Promos</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading promos...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No promos yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.title}</strong>
                                    <div className={styles.muted}>CTA: {item.cta.length} actions</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Promo' : 'New Promo'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="promo-title">Title</label>
                            <input
                                id="promo-title"
                                value={form.title}
                                onChange={(event) => setForm({ ...form, title: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="promo-description">Description</label>
                            <textarea
                                id="promo-description"
                                value={form.description ?? ''}
                                onChange={(event) => setForm({ ...form, description: event.target.value })}
                                maxLength={2000}
                            />
                        </div>
                        <ImagePickerField
                            label="Image URL"
                            value={form.imageUrl}
                            onChange={(next) => setForm({ ...form, imageUrl: next })}
                            token={token}
                            required
                            helperText="Pick from the image library or paste an external URL."
                            preferredVariantType={1}
                        />
                        <div className={styles.field}>
                            <label htmlFor="promo-start">Start Date</label>
                            <AdminDateTimeInput
                                id="promo-start"
                                value={form.startDate ?? ''}
                                includeTime
                                onChange={(value) => setForm({ ...form, startDate: value })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="promo-end">End Date</label>
                            <AdminDateTimeInput
                                id="promo-end"
                                value={form.endDate ?? ''}
                                includeTime
                                onChange={(value) => setForm({ ...form, endDate: value })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="promo-sort">Sort Order</label>
                            <input
                                id="promo-sort"
                                type="number"
                                value={form.sortOrder}
                                onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                                />
                                {' '}Active
                            </label>
                        </div>

                        <div className={styles.field}>
                            <label>CTA Actions</label>
                            {form.cta.length === 0 ? (
                                <div className={styles.muted}>No actions yet.</div>
                            ) : (
                                form.cta.map((action, index) => (
                                    <div key={`${action.type}-${index}`} className={styles.listItem}>
                                        <div style={{ flex: 1 }}>
                                            <div className={styles.field}>
                                                <label>Type</label>
                                                <select
                                                    value={action.type}
                                                    onChange={(event) => handleActionChange(index, 'type', event.target.value)}
                                                >
                                                    {promoActionTypes.map((type) => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className={styles.field}>
                                                <label>Label</label>
                                                <input
                                                    value={action.label}
                                                    onChange={(event) => handleActionChange(index, 'label', event.target.value)}
                                                    maxLength={200}
                                                />
                                            </div>
                                            <div className={styles.field}>
                                                <label>Target Type</label>
                                                <select
                                                    value={action.targetType}
                                                    onChange={(event) => handleActionChange(index, 'targetType', event.target.value)}
                                                >
                                                    {promoTargetTypes.map((type) => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className={styles.field}>
                                                <label>Target</label>
                                                <input
                                                    value={action.target}
                                                    onChange={(event) => handleActionChange(index, 'target', event.target.value)}
                                                    placeholder={action.targetType === 'internal' ? '/watch?v=DIe4rdPHP4pvC' : 'https://example.com/page'}
                                                    maxLength={2000}
                                                />
                                                <div className={styles.muted}>
                                                    {action.targetType === 'internal'
                                                        ? 'Internal: use app routes only (for watch, use /watch?v=<shortcode>).'
                                                        : 'External: use full URL including https://.'}
                                                </div>
                                            </div>
                                        </div>
                                        <button type="button" className={styles.danger} onClick={() => removeAction(index)}>
                                            Remove
                                        </button>
                                    </div>
                                ))
                            )}
                            <div className={styles.actions}>
                                <button type="button" className={styles.secondary} onClick={addAction}>Add Action</button>
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Promo' : 'Create Promo'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminPromos;
